"use client";

import React, { forwardRef } from "react";
import { Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";

type SearchBarVariant = "body" | "header";

interface SearchBarProps {
  variant?: SearchBarVariant;
  onSubmit?: (e: React.FormEvent) => void;
  loading?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  className?: string;
}

export function SearchBarDivider({
  variant = "body",
}: {
  variant?: SearchBarVariant;
}) {
  return (
    <div
      className={cn(
        "shrink-0 border-l",
        variant === "header"
          ? "h-8 border-muted-foreground/30"
          : "h-8 border-pure-black/20"
      )}
    />
  );
}

/** Small horizontal dash used between date ranges (e.g., "Mon 4/2 — Mon 4/3") */
export function SearchBarDash({
  variant = "body",
}: {
  variant?: SearchBarVariant;
}) {
  return (
    <div
      className={cn(
        "h-px shrink-0",
        variant === "header" ? "w-2.5 bg-foreground" : "w-2.5 bg-pure-black"
      )}
    />
  );
}

const SearchBar = forwardRef<HTMLFormElement, SearchBarProps>(
  (
    { variant = "body", onSubmit, loading, disabled, children, className },
    ref
  ) => {
    const isHeader = variant === "header";

    return (
      <form
        ref={ref}
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit?.(e);
        }}
        className={cn(
          "flex w-full items-center rounded-xl border border-input",
          isHeader ? "bg-muted pr-1.5" : "h-16 bg-pure-white pr-1",
          className
        )}
      >
        <div
          className={cn(
            "flex h-full min-w-0 flex-1 items-center gap-4",
            isHeader ? "" : "pl-1"
          )}
        >
          {children}
        </div>

        <Button
          type="submit"
          aria-label="Search"
          variant="search-gold"
          disabled={loading || disabled}
          loading={loading}
          loadingClassName="m-0"
          className={cn("shrink-0 p-0", isHeader ? "h-8 w-8" : "h-14 w-14")}
        >
          {loading ? null : (
            <Search className={cn(isHeader ? "h-3.5 w-3.5" : "h-5 w-5")} />
          )}
        </Button>
      </form>
    );
  }
);

SearchBar.displayName = "SearchBar";

export function formatCompactDate(dateStr: string): string {
  const date = new Date(dateStr + "T00:00:00");
  return format(date, "EEE M/d");
}

export default SearchBar;
